/**
 * ProjectsShowcasePage - Showcase of 1000 Erasmus+ project templates
 * Allows browsing, searching, and filtering projects
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  ContactMail as ContactIcon,
  ArrowBack as BackIcon,
  Tag as TagIcon
} from '@mui/icons-material';
import { API_URL } from '../config';

interface ProjectTemplate {
  code: string;
  title: string;
  problem_statement: string;
  main_objective: string;
  target_groups: string[];
  expected_results: string[];
  innovation_elements: string;
  eu_priorities: string[];
}

interface ProjectsShowcasePageProps {
  onBack: () => void;
}

interface TagWithCount {
  tag: string;
  count: number;
}

const ProjectsShowcasePage: React.FC<ProjectsShowcasePageProps> = ({ onBack }) => {
  const [projects, setProjects] = useState<ProjectTemplate[]>([]);
  const [allTags, setAllTags] = useState<TagWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectTemplate | null>(null);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [contactStatus, setContactStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [page, setPage] = useState(1);
  const [totalProjects, setTotalProjects] = useState(0);
  const projectsPerPage = 100; // Show 100 projects initially
  const [showAllTags, setShowAllTags] = useState(false);

  // Load projects and tags on mount
  useEffect(() => {
    loadTags();
    loadProjects(true); // Start with random order
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload tags when selectedTags changes (for recursive filtering)
  useEffect(() => {
    loadTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags.join(',')]); // Use join to detect array changes

  // Reload projects when search, tags, or page changes
  useEffect(() => {
    // Use a small delay for search to avoid too many requests while typing
    const timeoutId = setTimeout(() => {
      if (!loading) {
        loadProjects(false);
      }
    }, searchTerm ? 500 : 0); // 500ms delay when searching, immediate when clearing
    
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, selectedTags.join(','), page]);

  const loadTags = async () => {
    try {
      // If tags are selected, pass them to get filtered counts
      // For multiple tags, we need to filter by all selected tags
      let url = `${API_URL}/api/projects/showcase/tags`;
      if (selectedTags.length > 0) {
        url += `?selected_tags=${encodeURIComponent(JSON.stringify(selectedTags))}`;
      }
      
      console.log('🔄 Loading tags with selected tags:', selectedTags);
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        const tags = data.tags || [];
        console.log('✅ Tags loaded:', {
          total: tags.length,
          selected: selectedTags.length,
          tags: tags.slice(0, 5).map((t: TagWithCount) => `${t.tag} (${t.count})`)
        });
        
        // Filter out tags with count 0 (no projects in common)
        const validTags = tags.filter((tag: TagWithCount) => {
          const count = typeof tag === 'object' && 'count' in tag ? tag.count : 0;
          return count > 0;
        });
        
        console.log(`📊 Filtered tags: ${validTags.length} with count > 0 (from ${tags.length} total)`);
        setAllTags(validTags);
      } else {
        console.error('❌ Failed to load tags:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('❌ Error loading tags:', error);
    }
  };

  const loadProjects = async (random: boolean = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm && searchTerm.trim()) {
        params.append('search', searchTerm.trim());
        console.log('🔍 Search term:', searchTerm.trim());
      }
      // Support multiple tags - pass as JSON array
      if (selectedTags.length > 0) {
        params.append('tags', JSON.stringify(selectedTags));
      }
      if (random) {
        params.append('random', 'true');
      }
      // Load 100 projects initially (or more if filtered)
      const limit = searchTerm || selectedTags.length > 0 ? projectsPerPage : 100;
      params.append('limit', limit.toString());
      params.append('offset', ((page - 1) * projectsPerPage).toString());

      console.log('🔍 Loading projects from:', `${API_URL}/api/projects/showcase?${params.toString()}`);
      const response = await fetch(`${API_URL}/api/projects/showcase?${params.toString()}`);
      
      if (!response.ok) {
        console.error('❌ Response not OK:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        return;
      }
      
      const data = await response.json();
      console.log('✅ Projects loaded:', {
        count: data.projects?.length || 0,
        total: data.total || 0,
        projects: data.projects?.slice(0, 3) // Log first 3 for debugging
      });
      
      setProjects(data.projects || []);
      setTotalProjects(data.total || 0);
      
      if (!data.projects || data.projects.length === 0) {
        console.warn('⚠️ No projects found in response');
      }
    } catch (error) {
      console.error('❌ Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTagClick = (tag: string) => {
    console.log('🏷️ Tag clicked:', tag);
    setSelectedTags(prev => {
      const newSelection = prev.includes(tag)
        ? prev.filter(t => t !== tag) // Remove tag if already selected
        : [...prev, tag]; // Add tag if not selected
      
      console.log('📝 New tag selection:', newSelection);
      return newSelection;
    });
    setPage(1); // Reset to first page
  };

  const handleProjectClick = (project: ProjectTemplate) => {
    setSelectedProject(project);
    setContactFormData({
      name: '',
      email: '',
      message: `I'm interested in Erasmus+ project ${project.code}: ${project.title}\n\nPlease provide more information about this project.\n\n`
    });
    setContactDialogOpen(true);
  };

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setContactStatus('sending');

    try {
      const response = await fetch('https://formspree.io/f/xdkqlolb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: contactFormData.name,
          email: contactFormData.email,
          message: contactFormData.message,
          project_code: selectedProject?.code || '',
          _subject: `Interest in Erasmus+ Project: ${selectedProject?.code || ''}`
        }),
      });

      if (response.ok) {
        setContactStatus('success');
        setTimeout(() => {
          setContactDialogOpen(false);
          setContactStatus('idle');
          setContactFormData({ name: '', email: '', message: '' });
        }, 2000);
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      setContactStatus('error');
    }
  };

  // Get popular tags (top 10-15 by count) to show first
  const popularTags = useMemo(() => {
    // Sort by count and centrality, take top tags
    const sorted = [...allTags].sort((a, b) => {
      const countA = (typeof a === 'object' && a !== null && 'count' in a) ? (typeof a.count === 'number' ? a.count : 0) : 0;
      const countB = (typeof b === 'object' && b !== null && 'count' in b) ? (typeof b.count === 'number' ? b.count : 0) : 0;
      const centralityA = (typeof a === 'object' && a !== null && 'centrality' in a) ? (typeof a.centrality === 'number' ? a.centrality : 0) : 0;
      const centralityB = (typeof b === 'object' && b !== null && 'centrality' in b) ? (typeof b.centrality === 'number' ? b.centrality : 0) : 0;
      
      // Sort by count first, then centrality
      if (countB !== countA) return countB - countA;
      return centralityB - centralityA;
    });
    return sorted.slice(0, 15); // Top 15 most popular
  }, [allTags]);

  // Get other tags (excluding popular ones)
  const otherTags = useMemo(() => {
    const popularTagNames = new Set(
      popularTags.map(t => typeof t === 'object' && 'tag' in t ? t.tag : String(t))
    );
    return allTags.filter(t => {
      const tagName = typeof t === 'object' && 'tag' in t ? t.tag : String(t);
      return !popularTagNames.has(tagName);
    });
  }, [allTags, popularTags]);

  // Get tags to display - exclude popular tags (they're shown separately)
  // Only show "other tags" to avoid duplicates
  const displayedTags = useMemo(() => {
    if (showAllTags) {
      return otherTags; // Show all other tags (popular ones are shown separately)
    }
    // Show first 5-10 other tags (popular ones are shown separately)
    return otherTags.slice(0, 5);
  }, [otherTags, showAllTags]);

  // Get total count of tags (for display)
  const totalTagsCount = useMemo(() => {
    return allTags.length;
  }, [allTags]);

  const totalPages = Math.ceil(totalProjects / projectsPerPage);

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #003399 0%, #0052cc 100%)', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Button
            startIcon={<BackIcon />}
            onClick={onBack}
            sx={{ mb: 2, color: 'white' }}
          >
            Back
          </Button>
          <Typography variant="h3" component="h1" sx={{ color: 'white', fontWeight: 700, mb: 2 }}>
            Erasmus+ Projects Showcase
          </Typography>
          <Typography variant="h6" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 4 }}>
            {loading ? (
              'Loading projects...'
            ) : totalProjects > 0 ? (
              `Browse ${totalProjects} ready-to-use Erasmus+ project templates`
            ) : (
              'No projects available. Please load project templates first.'
            )}
          </Typography>
        </Box>

        {/* Search and Filters */}
        <Paper elevation={8} sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <TextField
            fullWidth
            placeholder="Search projects by title, problem, or objective..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          {/* Tags Filter */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                <TagIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Filter by Topic ({allTags.length} available):
              </Typography>
              {allTags.length > 20 && (
                <Button
                  size="small"
                  onClick={() => setShowAllTags(!showAllTags)}
                  sx={{
                    textTransform: 'none',
                    color: '#003399',
                    '&:hover': {
                      bgcolor: 'rgba(0, 51, 153, 0.1)'
                    }
                  }}
                >
                  {showAllTags ? 'Show Less' : `Show All (${allTags.length})`}
                </Button>
              )}
            </Box>
            {/* Popular Tags Section */}
            {popularTags.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600, color: '#003399' }}>
                  Popular Topics:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {popularTags.map((tagData) => {
                    const tag = typeof tagData === 'object' && tagData !== null && 'tag' in tagData 
                      ? tagData.tag 
                      : String(tagData);
                    const count = typeof tagData === 'object' && tagData !== null && 'count' in tagData
                      ? tagData.count
                      : undefined;
                    const isSelected = selectedTags.includes(tag);
                    
                    return (
                      <Chip
                        key={tag}
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <span>{tag}</span>
                            {count !== undefined && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: '0.75rem',
                                  fontWeight: isSelected ? 700 : 500,
                                  opacity: isSelected ? 1 : 0.7,
                                  ml: 0.5
                                }}
                              >
                                ({count})
                              </Typography>
                            )}
                          </Box>
                        }
                        onClick={() => handleTagClick(tag)}
                        sx={{
                          cursor: 'pointer',
                          ...(isSelected
                            ? {
                                bgcolor: '#003399',
                                color: 'white',
                                fontWeight: 600,
                                border: '2px solid #003399',
                                '&:hover': {
                                  bgcolor: '#002266',
                                  borderColor: '#002266'
                                }
                              }
                            : {
                                bgcolor: 'white',
                                color: '#003399',
                                border: '1px solid #003399',
                                '&:hover': {
                                  bgcolor: 'rgba(0, 51, 153, 0.1)',
                                  borderColor: '#002266',
                                  borderWidth: '2px'
                                }
                              })
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Selected Tags Section */}
            {selectedTags.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600, color: '#003399' }}>
                  Selected Topics:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {selectedTags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onClick={() => handleTagClick(tag)}
                      sx={{
                        cursor: 'pointer',
                        bgcolor: '#003399',
                        color: 'white',
                        fontWeight: 600,
                        border: '2px solid #003399',
                        '&:hover': {
                          bgcolor: '#002266',
                          borderColor: '#002266'
                        }
                      }}
                    />
                  ))}
                  <Chip
                    label={`Clear All (${selectedTags.length})`}
                    onClick={() => {
                      setSelectedTags([]);
                      setPage(1);
                    }}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: '#ff6b6b',
                      color: 'white',
                      fontWeight: 600,
                      border: '2px solid #ff6b6b',
                      '&:hover': {
                        bgcolor: '#ee5a5a',
                        borderColor: '#ee5a5a'
                      }
                    }}
                  />
                </Box>
              </Box>
            )}

            {/* Other Tags Section */}
            {displayedTags.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600, color: '#003399' }}>
                  {showAllTags ? 'All Topics' : 'More Topics'}:
                </Typography>
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1,
                    maxHeight: showAllTags ? 'none' : '120px',
                    overflow: showAllTags ? 'visible' : 'hidden',
                    position: 'relative'
                  }}
                >
                  {displayedTags.map((tagData) => {
                // Handle both TagWithCount objects and plain strings (for backward compatibility)
                const tag = typeof tagData === 'object' && tagData !== null && 'tag' in tagData 
                  ? tagData.tag 
                  : String(tagData);
                const count = typeof tagData === 'object' && tagData !== null && 'count' in tagData
                  ? tagData.count
                  : undefined;
                const isSelected = selectedTags.includes(tag);
                
                return (
                  <Chip
                    key={tag}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>{tag}</span>
                        {count !== undefined && (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: isSelected ? 700 : 500,
                              opacity: isSelected ? 1 : 0.7,
                              ml: 0.5
                            }}
                          >
                            ({count})
                          </Typography>
                        )}
                      </Box>
                    }
                    onClick={() => handleTagClick(tag)}
                    sx={{
                      cursor: 'pointer',
                      ...(isSelected
                        ? {
                            bgcolor: '#003399',
                            color: 'white',
                            fontWeight: 600,
                            border: '2px solid #003399',
                            '&:hover': {
                              bgcolor: '#002266',
                              borderColor: '#002266'
                            }
                          }
                        : {
                            bgcolor: 'white',
                            color: '#003399',
                            border: '1px solid #003399',
                            '&:hover': {
                              bgcolor: 'rgba(0, 51, 153, 0.1)',
                              borderColor: '#002266',
                              borderWidth: '2px'
                            }
                          })
                    }}
                  />
                );
                  })}
                </Box>
              </Box>
            )}
            {!showAllTags && totalTagsCount > 20 && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Showing 20 of {totalTagsCount} topics. Click "Show All" to see all available topics.
                  {selectedTags.length > 0 && ` Counts are filtered by ${selectedTags.length} selected tag(s).`}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Projects Grid */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress size={60} sx={{ color: 'white' }} />
          </Box>
        ) : projects.length === 0 ? (
          <Paper elevation={8} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
            <Typography variant="h6" color="text.secondary">
              No projects found. Try adjusting your search or filter.
            </Typography>
          </Paper>
        ) : (
          <>
            <Grid container spacing={3}>
              {projects.map((project) => (
                <Grid item xs={12} sm={6} md={4} key={project.code}>
                  <Card
                    elevation={8}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 3,
                      border: '1px solid rgba(0, 51, 153, 0.1)',
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px) scale(1.02)',
                        boxShadow: '0 12px 24px rgba(0, 51, 153, 0.2)',
                        borderColor: '#003399'
                      }
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1, pb: 1 }}>
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" sx={{ 
                          display: 'block', 
                          mb: 1, 
                          color: '#003399', 
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5
                        }}>
                          {project.code}
                        </Typography>
                        <Typography variant="h6" component="h3" gutterBottom sx={{ 
                          fontWeight: 700, 
                          color: '#003399',
                          lineHeight: 1.3,
                          mb: 2
                        }}>
                          {project.title}
                        </Typography>
                      </Box>
                      
                      <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                          mb: 2, 
                          display: '-webkit-box', 
                          WebkitLineClamp: 4, 
                          WebkitBoxOrient: 'vertical', 
                          overflow: 'hidden',
                          lineHeight: 1.6,
                          minHeight: '80px'
                        }}
                      >
                        {project.problem_statement || project.main_objective || 'No description available.'}
                      </Typography>

                      {/* Tags */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                        {project.eu_priorities?.slice(0, 2).map((priority, idx) => (
                          <Chip
                            key={idx}
                            label={priority}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(0, 51, 153, 0.1)',
                              color: '#003399',
                              border: '1px solid #003399',
                              fontWeight: 500
                            }}
                          />
                        ))}
                        {project.target_groups?.slice(0, 1).map((group, idx) => (
                          <Chip
                            key={idx}
                            label={group}
                            size="small"
                            sx={{
                              fontSize: '0.7rem',
                              bgcolor: 'rgba(156, 39, 176, 0.1)',
                              color: '#9c27b0',
                              border: '1px solid #9c27b0',
                              fontWeight: 500
                            }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<ContactIcon />}
                        onClick={() => handleProjectClick(project)}
                        sx={{
                          bgcolor: '#003399',
                          '&:hover': { bgcolor: '#002266' }
                        }}
                      >
                        Get This Project
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, newPage) => setPage(newPage)}
                  color="primary"
                  size="large"
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'white',
                      '&.Mui-selected': {
                        bgcolor: 'white',
                        color: '#003399'
                      }
                    }
                  }}
                />
              </Box>
            )}
          </>
        )}

        {/* Contact Dialog */}
        <Dialog
          open={contactDialogOpen}
          onClose={() => {
            if (contactStatus !== 'sending') {
              setContactDialogOpen(false);
              setContactStatus('idle');
            }
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ContactIcon sx={{ color: '#003399' }} />
              <Typography variant="h6">Request Project Information</Typography>
            </Box>
            {selectedProject && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Project: {selectedProject.code} - {selectedProject.title}
              </Typography>
            )}
          </DialogTitle>
          <form onSubmit={handleContactSubmit}>
            <DialogContent>
              {contactStatus === 'success' && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Thank you! We'll contact you soon about this project.
                </Alert>
              )}
              {contactStatus === 'error' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Failed to send message. Please try again.
                </Alert>
              )}

              <TextField
                fullWidth
                label="Your Name"
                value={contactFormData.name}
                onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
                required
                margin="normal"
                disabled={contactStatus === 'sending'}
              />
              <TextField
                fullWidth
                label="Your Email"
                type="email"
                value={contactFormData.email}
                onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
                required
                margin="normal"
                disabled={contactStatus === 'sending'}
              />
              <TextField
                fullWidth
                label="Your Message"
                value={contactFormData.message}
                onChange={(e) => setContactFormData({ ...contactFormData, message: e.target.value })}
                required
                multiline
                rows={6}
                margin="normal"
                disabled={contactStatus === 'sending'}
                helperText={`Project code ${selectedProject?.code || ''} will be included in your message`}
              />
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => {
                  setContactDialogOpen(false);
                  setContactStatus('idle');
                }}
                disabled={contactStatus === 'sending'}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={contactStatus === 'sending'}
                startIcon={contactStatus === 'sending' ? <CircularProgress size={20} /> : <ContactIcon />}
                sx={{ bgcolor: '#003399', '&:hover': { bgcolor: '#002266' } }}
              >
                {contactStatus === 'sending' ? 'Sending...' : 'Send Request'}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ProjectsShowcasePage;

